import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { Controller, FormProvider, useForm, useFieldArray } from 'react-hook-form'
import {
  CreatePayableInput,
  PayableStatus,
  PaymentMethod,
  useCreateAccountPayableMutation,
  useFetchAchOrEftNoOfEntryLazyQuery,
  useFetchAllAccountsLazyQuery,
  useFetchOperatingAccountLazyQuery,
  useFetchRetainerAccountLazyQuery,
  useFetchVoucherOfPayableEntryQuery,
  useFetchZescrowAccountLazyQuery,
  useFindPayableByCheckNumberLazyQuery,
  useUpdateAccountPayableMutation
} from '../../../../generated'
import {
  CANCEL,
  GRAPHQL_QUERY_POLICY,
  GRAPHQL_QUERY_POLICY_CACHE_FIRST,
  PAYABLE_ENTRY_PAYMENT_METHODS
} from '../../../constants'
import { createAccountPayableSchema } from '../../../validationSchema'
import { formatCurrency, getProperPaymentMethod } from '../../../utils'
import AccountDropdown from '../../common/AccountDropdown'
import { Alert } from '../../common/Alert'
import { CustomController } from '../../common/CustomController'
import { CustomModal } from '../../common/CustomModal'
import MatterDropdownWithForm from '../../common/MatterDropdownWithForm'
import OfficeAutoComplete from '../../offices/OfficeAutocomplete'
import VendorAutoComplete from '../../vendors/VendorAutoComplete'
import DepositClientDropdown from '../depositFund/DepositClientDropdown'
import { CreatePayableFormValues } from '../../../interfaceTypes/forms'
import { useEffect, useState } from 'react'
import PaymentMethodController from '../../common/PaymentMethodController'
import { AddAccountPayableModalProps } from '../../../interfaceTypes/props'
import CheckboxController from '../../common/CheckboxController'
import DeleteIcon from '@mui/icons-material/Delete'
import SearchOutlined from '@mui/icons-material/SearchOutlined'
import { CustomDatePickerController } from '../../common/CustomDatePickerRController'
import {
  ESCROW_BANK_SORT_CODE,
  PayableType,
  PAYABLE_TYPE_LABELS,
  PAYABLE_GL_SORT_CODE,
  TRUST_BANK_SORT_CODE
} from './constants'
import PayableConfirmationModal from './PayableConfirmationModal'
import PayableLookupModal from './PayableLookupModal'
import { useGetClientBalanceInAccountLazyQuery } from '../../../../generated'
import dayjs from 'dayjs'

const toAccountOption = (acc: { id: string; name: string; sortCode: string }) => ({
  value: acc.id,
  label: `(#${acc.sortCode}) ${acc.name || ''}`
})

const AddAccountPayableModal = ({
  isOpen,
  handleClose,
  refetch: refetchPayables,
  currentPayable
}: AddAccountPayableModalProps) => {
  const [accountBalance, setAccountBalance] = useState(currentPayable?.account?.currentBalance?.toFixed(2) ?? '0.00')
  const [payableType, setPayableType] = useState<PayableType>(PayableType.OTHER)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [pendingFormData, setPendingFormData] = useState<CreatePayableFormValues | null>(null)
  const [lookupOpen, setLookupOpen] = useState(false)
  const [checkNumberConflict, setCheckNumberConflict] = useState<{ id: string; voucherNo: string } | null>(null)
  const initialMatter = currentPayable?.lineItems?.find(item => item.litigation)?.litigation ?? null
  const initialLineItemMatters = currentPayable?.lineItems?.map(item => item.litigation ?? null) ?? []
  // Editing an already-paid payable updates its ledger in place on save, and it stays paid (the status
  // can't be toggled off through an edit). The server enforces the same locks as the fields below:
  //  - paid: the amount, bank account and each line's matter are frozen — the cash side cannot move;
  //  - reconciled, additionally: pay date, payment method and check/reference number are frozen;
  //  - a line whose matter's expense entry is already on a bill is frozen and cannot be removed.
  const isEditingPaid = currentPayable?.status === PayableStatus.Paid
  const isEditingReconciled = isEditingPaid && !!currentPayable?.isReconciled
  const billedCaseIds = new Set(currentPayable?.billedCaseIds ?? [])
  const isLineBilled = (idx: number) => {
    const caseId = currentPayable?.lineItems?.[idx]?.litigation?.id
    return isEditingPaid && !!caseId && billedCaseIds.has(caseId)
  }

  const methods = useForm<CreatePayableFormValues>({
    mode: 'onChange',
    resolver: yupResolver(createAccountPayableSchema),
    // A paid payable's lines can't be added, and a billed one can't be removed, so $0 is one way a line
    // is emptied there. New and unpaid payables still reject $0 lines.
    context: { allowZeroLineAmount: isEditingPaid },
    defaultValues: {
      payDate: new Date().toISOString().split('T')[0],
      invoiceDate: new Date().toISOString().split('T')[0],
      amount: 0,
      isPaid: false,
      multipleLineItems: false,
      paymentMethod: '',
      matter: '',
      lineItems: [{ matter: '', office: undefined, glAccount: undefined, amount: 0, description: '' }]
    }
  })

  const { control, formState, setValue, reset, watch, getValues } = methods
  const multipleLineItems = watch('multipleLineItems')

  // The payable amount and its line items are stored independently: the amount drives the bank leg
  // of the journal entry, the line items drive the expense legs. When they disagree the entry posts
  // unbalanced and quietly throws the Balance Sheet out (nine 2026 payables did, $49,739.12 in
  // total). Surface the running comparison so a mismatch is visible while editing, not just on save.
  const watchedAmount = watch('amount')
  const watchedLineItems = watch('lineItems')
  const lineItemsTotalCents = (watchedLineItems || []).reduce(
    (sum, item) => sum + Math.round((Number(item?.amount) || 0) * 100),
    0
  )
  const payableAmountCents = Math.round((Number(watchedAmount) || 0) * 100)
  const lineItemsDifferenceCents = lineItemsTotalCents - payableAmountCents
  const hasLineItemMismatch = multipleLineItems && lineItemsDifferenceCents !== 0
  const selectedVendor = watch('vendor')
  const selectedVendorOffice = watch('office')
  const bankAccount = watch('bankAccount')
  const clientOption = watch('lineItems.0.client')
  const caseId = watch('matter')
  const isTrustOrEscrow = payableType === PayableType.TRUST || payableType === PayableType.ESCROW

  // Setup useFieldArray for lineItems
  const {
    fields: lineItemFields,
    append,
    remove,
    replace
  } = useFieldArray({
    control,
    name: 'lineItems'
  })

  useFetchVoucherOfPayableEntryQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError(error) {
      console.log('Error fetching voucherNo', error.message)
    },
    onCompleted(data) {
      const { fetchVoucherOfPayableEntry } = data || {}
      const { voucherNo } = fetchVoucherOfPayableEntry || {}
      if (!currentPayable?.id) {
        setValue('voucherNo', voucherNo)
      }
    }
  })

  const [fetchAchOrEftNo] = useFetchAchOrEftNoOfEntryLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError(error) {
      console.log('Error fetching ach or eft no', error.message)
    },
    onCompleted(data) {
      const { fetchAchOrEftNoOfEntry } = data || {}
      const { achOrEftNo } = fetchAchOrEftNoOfEntry || {}
      if (!currentPayable?.id && !isNaN(Number(achOrEftNo))) setValue('checkOrAchNumber', achOrEftNo)
    }
  })

  const [findPayableByCheckNumber] = useFindPayableByCheckNumberLazyQuery({
    fetchPolicy: 'network-only',
    onError() {
      setCheckNumberConflict(null)
    },
    onCompleted(data) {
      const match = data?.findPayableByCheckNumber?.accountPayableEntry
      // Ignore a match against the payable currently being edited.
      if (match && match.id !== currentPayable?.id) {
        setCheckNumberConflict({ id: match.id, voucherNo: match.voucherNo })
      } else {
        setCheckNumberConflict(null)
      }
    }
  })

  const [fetchRetainerAccount] = useFetchRetainerAccountLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError() {},
    onCompleted(data) {
      if (data?.fetchRetainerAccount) {
        const acc = data.fetchRetainerAccount as {
          id: string
          name: string
          sortCode: string
          currentBalance?: number | null
        }
        setValue('bankAccount', toAccountOption(acc))
        setAccountBalance(acc.currentBalance != null ? Number(acc.currentBalance).toFixed(2) : '0.00')
      }
    }
  })

  const [fetchClientBalanceInAccount] = useGetClientBalanceInAccountLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onCompleted(data) {
      const balance = data?.getClientBalanceInAccount?.balance
      setAccountBalance(balance != null && !Number.isNaN(Number(balance)) ? Number(balance).toFixed(2) : '0.00')
    }
  })

  const [fetchZescrowAccount] = useFetchZescrowAccountLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError() {},
    onCompleted(data) {
      if (data?.fetchZescrowAccount) {
        const acc = data.fetchZescrowAccount as {
          id: string
          name: string
          sortCode: string
          currentBalance?: number | null
        }
        setValue('bankAccount', toAccountOption(acc))
        setAccountBalance(acc.currentBalance != null ? Number(acc.currentBalance).toFixed(2) : '0.00')
      }
    }
  })

  const [fetchOperatingAccount] = useFetchOperatingAccountLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError() {},
    onCompleted(data) {
      if (data?.fetchOperatingAccount) {
        const acc = data.fetchOperatingAccount as {
          id: string
          name: string
          sortCode: string
          currentBalance?: number | null
        }
        setValue('bankAccount', toAccountOption(acc))
        setAccountBalance(acc.currentBalance != null ? Number(acc.currentBalance).toFixed(2) : '0.00')
      }
    }
  })

  const [fetchAccountsBySortCode] = useFetchAllAccountsLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    variables: undefined,
    onError() {},
    onCompleted(data) {
      const accounts = (data?.fetchAllAccounts?.accounts ?? []).filter(Boolean)
      const first = accounts[0] as { id: string; name: string; sortCode: string } | undefined
      if (!first) return
      const option = toAccountOption(first)
      setValue('glAccount', option)
      if (multipleLineItems && lineItemFields.length > 0) {
        setValue('lineItems.0.glAccount', option)
      }
    }
  })

  const [createAccountPayable, { loading }] = useCreateAccountPayableMutation({
    onError(error) {
      Alert.error(error.message)
    },
    onCompleted(data) {
      const { createAccountPayable } = data || {}
      const { response } = createAccountPayable || {}
      const { status } = response || {}
      if (status === 200) {
        Alert.success('Voucher Generated!')
        setShowConfirmationModal(false)
        setPendingFormData(null)
        handleClose()
        refetchPayables?.()
        reset()
      }
    }
  })

  const [updateAccountPayable, { loading: updating }] = useUpdateAccountPayableMutation({
    onError(error) {
      Alert.error(error.message)
    },
    onCompleted(data) {
      const { updateAccountPayable } = data || {}
      const { response } = updateAccountPayable || {}
      const { status } = response || {}
      if (status === 200) {
        Alert.success('Voucher Updated!')
        setShowConfirmationModal(false)
        setPendingFormData(null)
        handleClose()
        refetchPayables && refetchPayables()
        reset()
      }
    }
  })

  const toLocalDateTime = (dateString: string): Date => {
    const now = dayjs()
    return dayjs(dateString)
      .hour(now.hour())
      .minute(now.minute())
      .second(now.second())
      .millisecond(now.millisecond())
      .toDate()
  }

  const submitPayable = async (data: CreatePayableFormValues) => {
    const {
      bankAccount,
      checkOrAchNumber,
      glAccount,
      office,
      vendor,
      matter,
      invoiceDate,
      payDate,
      isPaid,
      multipleLineItems,
      lineItems,
      amount: formAmount,
      ...rest
    } = data

    const invoiceDateTime = toLocalDateTime(invoiceDate)
    const payDateTime = toLocalDateTime(payDate)

    const isTrustOrEscrow = payableType === PayableType.TRUST || payableType === PayableType.ESCROW

    let input: CreatePayableInput = {
      amount: Number(formAmount),
      status: isPaid ? PayableStatus.Paid : PayableStatus.Unpaid,
      bankAccountId: bankAccount?.value,
      vendorId: vendor?.value,
      invoiceDate: invoiceDateTime,
      payDate: payDateTime,
      checkOrAchNumber: checkOrAchNumber || null,
      lineItems:
        (multipleLineItems || !!currentPayable?.id) && lineItems
          ? lineItems.map((item, idx) => ({
              id: item.id,
              // In single-line mode the visible GL/office/matter/amount fields are the top-level
              // ones, not lineItems[idx]. Read from them so edits actually persist (otherwise the
              // stale lineItems[0] values get submitted).
              amount: !multipleLineItems && idx === 0 ? Number(formAmount) : Number(item.amount),
              glAccountId: (!multipleLineItems && idx === 0 ? glAccount?.value : item.glAccount?.value) || '',
              officeId: (!multipleLineItems && idx === 0 ? office?.value : item.office?.value) || 0,
              litigationId: !multipleLineItems && idx === 0 ? matter : item.matter,
              description: item.description || undefined
            }))
          : [
              {
                amount: Number(formAmount),
                glAccountId: glAccount?.value,
                officeId: office?.value,
                litigationId: matter,
                description: lineItems?.[0]?.description || undefined,
                ...(isTrustOrEscrow && lineItems?.[0]?.client && { clientId: lineItems?.[0]?.client?.value })
              }
            ],
      ...rest
    }

    if (currentPayable?.id) {
      const lineItemsToDelete = currentPayable.lineItems?.filter(item => !lineItems?.some(l => l.id === item.id))
      await updateAccountPayable({
        variables: {
          input: {
            id: currentPayable.id,
            lineItemsToDelete: lineItemsToDelete?.map(item => item.id),
            ...input
          }
        }
      })
      reset(data)
    } else {
      await createAccountPayable({
        variables: {
          input
        }
      })
    }
    // Deliberately NOT clearing accountBalance here. Both mutations declare an onError handler, so
    // a failed save resolves rather than throwing and execution reaches this point with the dialog
    // still open — zeroing the balance then made every retry fail the `accountBalance < amount`
    // guard below with "Insufficient balance", stranding the user until they closed and reopened
    // the dialog. On success onCompleted already closes it, and the dialog is unmounted by its
    // parent, so there is nothing to reset either way.
    setShowConfirmationModal(false)
    setPendingFormData(null)
  }

  const onSubmit = async (data: CreatePayableFormValues) => {
    if (data.multipleLineItems) {
      // Cents, not dollars: repeated float addition drifts and used to let a payable through a
      // cent out. Note this guard is NOT conditional on how the rows were entered - the mismatch
      // that broke the Balance Sheet came from a later edit that appended rows to an existing
      // payable and left the original placeholder row in place.
      const sumCents = (data.lineItems || []).reduce(
        (acc, item) => acc + Math.round((Number(item?.amount) || 0) * 100),
        0
      )
      const amountCents = Math.round((Number(data.amount) || 0) * 100)
      if (sumCents <= 0) {
        Alert.error('Sum of line item amounts must be greater than 0')
        return
      }
      if (sumCents !== amountCents) {
        const difference = (sumCents - amountCents) / 100
        Alert.error(
          `Line items total $${(sumCents / 100).toFixed(2)} but the Amount is $${(amountCents / 100).toFixed(2)}. ` +
            `${difference > 0 ? 'Remove' : 'Add'} $${Math.abs(difference).toFixed(2)} so they match.`
        )
        return
      }
    }
    // Advisory only - it must never stop a payable being entered. Recording a payable is book-
    // keeping, and the firm routinely has to enter one before the money to cover it arrives; the
    // bank balance does not move until it is marked paid anyway. So this tells the user the
    // account looks short and then gets out of the way.
    //
    // Only worth saying when money is actually leaving: an unpaid payable moves nothing, and a
    // payable that was already paid was funded when it was paid (the live balance is the
    // post-payment one, so it would look short every time).
    if (data.isPaid && !isEditingPaid && Number(accountBalance) < Number(data.amount)) {
      Alert.warning(`This payable is more than the ${bankAccount?.label || 'selected account'} balance.`)
    }
    setPendingFormData(data)
    await submitPayable(data)
    // setShowConfirmationModal(true)
  }

  const handleConfirmedSubmit = async () => {
    if (!pendingFormData) return
    await submitPayable(pendingFormData)
  }

  const paymentMethod = watch('paymentMethod')
  const isMethodAchOrEft = paymentMethod === PaymentMethod.Ach || paymentMethod === PaymentMethod.Eft

  useEffect(() => {
    if (paymentMethod && isMethodAchOrEft)
      fetchAchOrEftNo({
        variables: {
          method: paymentMethod.toLocaleLowerCase()
        }
      })
    // Not set checkOrAchNumber if not in edit mode
    else if (!currentPayable?.id) setValue('checkOrAchNumber', '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAchOrEftNo, paymentMethod])

  const checkOrAchNumber = watch('checkOrAchNumber')

  // Warn (debounced) when the entered check number is already assigned to another payable on the
  // selected bank account. Skipped for ACH/EFT reference numbers (those are auto-assigned).
  useEffect(() => {
    const trimmed = (checkOrAchNumber || '').trim()
    const accountId = bankAccount?.value

    if (isMethodAchOrEft || !trimmed || !accountId) {
      setCheckNumberConflict(null)
      return
    }

    const handler = setTimeout(() => {
      findPayableByCheckNumber({
        variables: { checkNumber: trimmed, bankAccountId: accountId }
      })
    }, 400)

    return () => clearTimeout(handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkOrAchNumber, bankAccount?.value, isMethodAchOrEft])

  // This modal stays mounted while the detail page is open, so its form state survives a
  // close/reopen. Re-sync to the current payable every time the modal opens (not just when the
  // payable id changes) — otherwise stale local edits (e.g. a matter change that the backend
  // rejected) linger after the user closes and reopens the modal.
  useEffect(() => {
    if (isOpen && currentPayable) {
      const office = currentPayable.lineItems?.find(item => item.office)?.office
      const glAccount = currentPayable.lineItems?.find(item => item.glAccount)?.glAccount
      const matter = currentPayable.lineItems?.find(item => item.litigation)?.litigation
      const client = currentPayable.lineItems?.find(item => item.client)?.client
      reset({
        bankAccount: {
          label: currentPayable.account?.name || '',
          value: currentPayable.account?.id || ''
        },
        glAccount: {
          label: glAccount?.name || '',
          value: glAccount?.id || ''
        },
        office: {
          label: office?.name || '',
          value: office?.id || undefined
        },
        vendor: currentPayable?.vendor
          ? {
              label: currentPayable.vendor?.name || '',
              value: currentPayable.vendor?.id || 0,
              glAccount: {
                label: currentPayable.vendor.glAccount?.name,
                value: currentPayable.vendor.glAccount?.id
              }
            }
          : undefined,
        matter: matter?.id ?? '',
        checkOrAchNumber: currentPayable.checkOrAchNumber,
        voucherNo: currentPayable.voucherNo,
        description: currentPayable.description,
        payDate: currentPayable.payDate.split('T')[0],
        invoiceDate: currentPayable.invoiceDate.split('T')[0],
        amount: currentPayable.amount,
        isPaid: currentPayable.status === PayableStatus.Paid,
        paymentMethod: currentPayable.paymentMethod,
        multipleLineItems: (currentPayable?.lineItems && currentPayable.lineItems.length > 1) ?? false,
        lineItems: currentPayable.lineItems?.map(item => ({
          id: item?.id,
          glAccount: {
            label: item?.glAccount?.name,
            value: item?.glAccount?.id
          },
          office: {
            label: item?.office?.name,
            value: item?.office?.id
          },
          matter: item?.litigation?.id,
          amount: item?.amount,
          client: client
            ? {
                value: client.id
              }
            : undefined,
          description: item?.description || ''
        })) || [{ matter: '', office: undefined, glAccount: undefined, amount: 0, description: '' }]
      })
      setAccountBalance(currentPayable?.account?.currentBalance?.toFixed(2) ?? '0.00')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPayable?.id, isOpen])

  // The vendor's Profit Center + GL Account, as plain options. Undefined when the vendor
  // carries neither, which is the signal to leave whatever is already in the form alone.
  const vendorOfficeOption = selectedVendor?.office?.value
    ? { label: selectedVendor.office.label, value: selectedVendor.office.value }
    : undefined
  const vendorGlAccountOption = selectedVendor?.glAccount?.value
    ? { label: selectedVendor.glAccount.label, value: selectedVendor.glAccount.value }
    : undefined

  // Prefill Profit Center + GL Account from the vendor. Keyed on the vendor id ALONE.
  //
  // This used to be two effects that also depended on `multipleLineItems` and
  // `lineItemFields.length`, so "+ Add Line Item" re-ran them and they rewrote the vendor's
  // default over EVERY row -- silently discarding a GL Account or Profit Center the user had
  // just corrected on an existing row. Picking a vendor is the only event that should
  // (re)apply that vendor's defaults; the toggle and append paths below now carry values
  // across on their own.
  //
  // Header fields and rows are both written so that whichever mode the user switches to next
  // starts from the vendor just chosen rather than the previous one's leftovers.
  useEffect(() => {
    if (currentPayable) return
    if (!vendorOfficeOption && !vendorGlAccountOption) return

    if (vendorOfficeOption) setValue('office', vendorOfficeOption)
    if (vendorGlAccountOption) setValue('glAccount', vendorGlAccountOption)

    // getValues, not lineItemFields: the field array lags a render behind an append.
    ;(getValues('lineItems') || []).forEach((_, idx) => {
      if (vendorOfficeOption) setValue(`lineItems.${idx}.office`, vendorOfficeOption)
      if (vendorGlAccountOption) setValue(`lineItems.${idx}.glAccount`, vendorGlAccountOption)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVendor?.value])

  // When user toggles Multiple Line Items
  useEffect(() => {
    if (multipleLineItems) {
      // Dont set amount in edit mode as it will overwrite the existing value
      if (currentPayable?.id) return
      if (lineItemFields.length > 0) {
        const mainAmount = Number(getValues('amount')) || 0
        setValue('lineItems.0.amount', mainAmount)
        // Carry the header Profit Center / GL Account down into the first row. In single-line
        // mode those two header fields ARE the line item (see submitPayable), so an override
        // typed there has to survive the switch -- the vendor-default effect above used to
        // fire on this toggle and stamp the vendor's default back over it.
        const headerOffice = getValues('office')
        const headerGlAccount = getValues('glAccount')
        if (headerOffice) setValue('lineItems.0.office', headerOffice)
        if (headerGlAccount) setValue('lineItems.0.glAccount', headerGlAccount)
      }
    } else {
      // Unchecking: discard extra rows, keep only first item aligned to header amount
      const first = getValues('lineItems.0')
      if (lineItemFields.length > 1) {
        const mainAmount = Number(getValues('amount')) || 0
        replace([{ ...first, amount: mainAmount }])
      }
      // ...and lift that row's Profit Center / GL Account back up into the header fields,
      // which are what single-line mode actually submits. Without this, a value edited on
      // row 0 in multi mode was dropped on the way back out.
      if (!currentPayable?.id) {
        if (first?.office) setValue('office', first.office)
        if (first?.glAccount) setValue('glAccount', first.glAccount)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multipleLineItems])

  const handlePayableTypeChange = (newType: PayableType) => {
    setPayableType(newType)
    if (newType === PayableType.OTHER) {
      setValue('glAccount', undefined as unknown as CreatePayableFormValues['glAccount'])
      const items = getValues('lineItems') || []
      items.forEach((_, idx) => setValue(`lineItems.${idx}.glAccount`, undefined))
    } else {
      setValue('multipleLineItems', false)
      const amount = Number(getValues('amount')) || 0
      const office = getValues('office')
      const matter = getValues('matter')
      const description = getValues('description')
      replace([{ matter: matter || '', office, glAccount: undefined, amount, description: description || '' }])
    }
  }

  useEffect(() => {
    const accountId = bankAccount?.value
    const clientId = clientOption?.value

    if (!isTrustOrEscrow || !accountId || !clientId || !caseId) return

    fetchClientBalanceInAccount({
      variables: { getClientBalanceInAccountInput: { accountId, clientId, caseId } }
    })
  }, [isTrustOrEscrow, bankAccount?.value, clientOption?.value, payableType, fetchClientBalanceInAccount, caseId])

  // Auto-fill bank account (and GL for trust/escrow) when Payable Type changes (create mode only)
  useEffect(() => {
    if (currentPayable?.id) return
    if (payableType === PayableType.TRUST) {
      fetchRetainerAccount()
      fetchAccountsBySortCode({
        variables: {
          accountsInput: { isBankAccount: false, sortCode: PAYABLE_GL_SORT_CODE }
        }
      })
    } else if (payableType === PayableType.ESCROW) {
      fetchZescrowAccount()
      fetchAccountsBySortCode({
        variables: {
          accountsInput: { isBankAccount: false, sortCode: PAYABLE_GL_SORT_CODE }
        }
      })
    } else if (payableType === PayableType.OTHER) {
      fetchOperatingAccount()
    }
  }, [
    payableType,
    currentPayable?.id,
    fetchRetainerAccount,
    fetchZescrowAccount,
    fetchOperatingAccount,
    fetchAccountsBySortCode
  ])

  const handleAddLineItem = () => {
    const mainAmount = Number(getValues('amount')) || 0
    const lineItems = getValues('lineItems') || []
    const used = lineItems.reduce((sum, item) => sum + (Number(item?.amount) || 0), 0)
    const remaining = Math.max(0, Math.round((mainAmount - used) * 100) / 100)
    // Seed the NEW row from the vendor here. Previously an effect broadcast the vendor's
    // defaults across every row on each append, which is what wiped out per-row edits; only
    // the row being added should be prefilled.
    append({
      glAccount: vendorGlAccountOption,
      office: vendorOfficeOption,
      matter: '',
      amount: remaining,
      description: ''
    })
  }

  return (
    <CustomModal
      isOpen={isOpen}
      handleClose={handleClose}
      title={currentPayable?.id ? 'Edit Payable' : 'Add Payable'}
      maxWidth="xl"
      closeOnlyWithButton
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Grid container spacing={2} alignItems="flex-start">
            {isEditingPaid && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'warning.light',
                    backgroundColor: 'rgba(237, 108, 2, 0.08)'
                  }}
                >
                  <Typography variant="body2" color="warning.dark">
                    This payable is already paid. Saving will update its existing journal entries, vendor payment,
                    trust balances and unbilled matter expense entries in place (keeping their numbers and history).
                    The amount, bank account and matters can&apos;t change
                    {isEditingReconciled
                      ? ', and because the payment is reconciled neither can the pay date, payment method or check number'
                      : ''}
                    . Lines can be re-coded, re-amounted (including to $0) or removed, as long as they still add up to
                    the amount. Lines whose expense is already on a bill are locked, and no line can be added — use
                    Void &amp; Duplicate for that.
                  </Typography>
                </Box>
              </Grid>
            )}
            {/* Payable Type Radio Group */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Payable Type
              </Typography>
              <RadioGroup
                row
                value={payableType}
                onChange={e => handlePayableTypeChange(e.target.value as PayableType)}
              >
                {Object.entries(PAYABLE_TYPE_LABELS).map(([value, label]) => (
                  <FormControlLabel
                    key={value}
                    value={value}
                    control={<Radio />}
                    label={label}
                    // Payable type is fixed once a payable exists — it determines the trust/escrow/operating
                    // accounting path, so changing it on an existing entry is not allowed.
                    disabled={!!currentPayable?.id}
                  />
                ))}
              </RadioGroup>
            </Grid>

            {/* Row 1: Bank Account, Vendor, Amount */}
            <Grid item md={4} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
              <FormControl variant="outlined" fullWidth sx={{ my: 1 }}>
                <Controller
                  name="bankAccount"
                  control={control}
                  render={({ field }) => (
                    <AccountDropdown
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isEditingPaid}
                      setBalance={setAccountBalance}
                      searchFilters={{
                        isBankAccount: true,
                        ...(payableType === PayableType.TRUST && { sortCode: TRUST_BANK_SORT_CODE }),
                        ...(payableType === PayableType.ESCROW && { sortCode: ESCROW_BANK_SORT_CODE })
                      }}
                      errorMessage={
                        formState.errors.bankAccount?.value?.message || formState.errors.bankAccount?.message
                      }
                      label={'Bank Account'}
                    />
                  )}
                />
              </FormControl>
              <Typography variant="subtitle1" color="text.secondary" pl={1} mb={1.5}>
                Available Balance: <strong>{formatCurrency(Number(accountBalance))}</strong>
              </Typography>
            </Grid>
            <Grid item md={2} sm={12} xs={12}>
              <CustomController controllerLabel="Voucher No" controllerName="voucherNo" readOnly />
            </Grid>
            <Grid item md={4} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
              <VendorAutoComplete
                name="vendor"
                label={'Vendor'}
                currentVendor={currentPayable?.vendor ?? undefined}
                // On a new payable the cursor lands in Vendor so it can be typed straight away.
                // Not on edit — the vendor is already set there and typing would replace it.
                autoFocus={!currentPayable?.id}
                errorMessage={formState.errors.vendor?.value?.message || formState.errors.vendor?.message}
                // Auto-fill the GL account from the vendor only when creating. On an existing payable,
                // keep the GL account as-is so changing the vendor doesn't silently reassign it.
                setGlAccount={!currentPayable?.id}
                // Type the vendor's name or its ID and Tab on to Amount — no mouse, same as the
                // Bank Account field. Data entry here is high-volume and keyboard-driven.
                autoSelectOnTab
              />
            </Grid>
            <Grid item md={2} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
              <CustomController
                controllerName="amount"
                controllerLabel={'Amount'}
                fieldType="number"
                dollarAdornment
                required
                isDisabled={isEditingPaid}
              />
            </Grid>
            {/* Row 2: Voucher No, Invoice Date, Pay Date */}
            <Grid item md={2} sm={12} xs={12} mt={1}>
              <PaymentMethodController
                name="paymentMethod"
                label="Payment By"
                paymentMethods={PAYABLE_ENTRY_PAYMENT_METHODS}
                // Keyboard-first entry, like Bank Account and GL Account beside it: type the
                // method and Tab straight on to Check No without opening the menu.
                searchable
                disabled={isEditingReconciled}
              />
            </Grid>
            <Grid item md={2} sm={12} xs={12}>
              <CustomController
                controllerLabel={isMethodAchOrEft ? 'Reference No' : 'Check No'}
                controllerName="checkOrAchNumber"
                readOnly={isMethodAchOrEft}
                isDisabled={isEditingReconciled}
              />
              {checkNumberConflict && (
                <Typography
                  variant="caption"
                  color="warning.main"
                  sx={{ display: 'block', mt: 0.5, lineHeight: 1.3 }}
                >
                  Check number {checkOrAchNumber} is already assigned to{' '}
                  <MuiLink
                    href={`/accounts/payable/${checkNumberConflict.voucherNo}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontWeight: 600, cursor: 'pointer' }}
                  >
                    Payable #{checkNumberConflict.voucherNo}
                  </MuiLink>
                </Typography>
              )}
            </Grid>
            <Grid item md={2} sm={12} xs={12}>
              <CustomDatePickerController
                controllerName="invoiceDate"
                controllerLabel="Invoice Date"
                required
                defaultValue={new Date()}
              />
            </Grid>
            <Grid item md={2} sm={12} xs={12}>
              <CustomDatePickerController
                controllerName="payDate"
                controllerLabel="Pay Date"
                required
                defaultValue={new Date()}
                isDisabled={isEditingReconciled}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <Box display="flex" alignItems="flex-start" gap={0.5}>
                <CustomController
                  controllerName="description"
                  controllerLabel="Notes / Invoice No."
                  fieldType="text"
                  sx={{ my: 1, flex: 1 }}
                />
                <Tooltip title="Look up old payables based on invoice number">
                  <span>
                    <IconButton onClick={() => setLookupOpen(true)} disabled={!watch('description')} sx={{ mt: 2 }}>
                      <SearchOutlined />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item md={12} sm={12} xs={12}>
              <CheckboxController
                label={'Multiple Line Items'}
                name="multipleLineItems"
                sx={{}}
                // Adding/removing lines isn't allowed when editing a paid payable (the backend updates
                // each existing line's ledger rows in place), so lock this toggle in that case.
                disabled={payableType === PayableType.TRUST || payableType === PayableType.ESCROW || isEditingPaid}
              />
            </Grid>

            {multipleLineItems ? (
              <Grid item xs={12}>
                <Grid container sx={{ paddingTop: 0 }}>
                  {lineItemFields.map((field, idx) => (
                    <Grid
                      container
                      spacing={1}
                      key={field.id}
                      alignItems="center"
                      sx={{
                        mb: 1,
                        mt: 1,
                        py: 2,
                        px: 1.5,
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      <Grid item xs={12} md={1.8}>
                        <Box sx={{ pt: 0 }}>
                          <Controller
                            name={`lineItems.${idx}.matter`}
                            control={control}
                            render={({ field, fieldState }) => (
                              <MatterDropdownWithForm
                                {...field}
                                name={`lineItems.${idx}.matter`}
                                userId=""
                                label="Matter"
                                disabled={isEditingPaid}
                                autoSelectOnTab
                                helperText={formState.errors.lineItems?.[idx]?.matter?.message}
                                initialCase={initialLineItemMatters[idx]}
                              />
                            )}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={1.8}>
                        <Box sx={{ pt: 0 }}>
                          <Controller
                            name={`lineItems.${idx}.office`}
                            control={control}
                            render={({ field }) => (
                              <OfficeAutoComplete
                                {...field}
                                name={`lineItems.${idx}.office`}
                                label="Profit Center"
                                disabled={isLineBilled(idx)}
                                sx={{ paddingTop: 0 }}
                                currentOffice={
                                  currentPayable?.lineItems?.[idx]
                                    ? {
                                        id: currentPayable.lineItems[idx].office.id,
                                        name: currentPayable.lineItems[idx].office.name
                                      }
                                    : field?.value?.value
                                    ? {
                                        id: field.value?.value,
                                        name: field.value?.label
                                      }
                                    : undefined
                                }
                                autoSelectOnTab
                              />
                            )}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={2.8}>
                        <Box sx={{ pt: 0 }}>
                          <Controller
                            name={`lineItems.${idx}.glAccount`}
                            control={control}
                            render={({ field }) => (
                              <AccountDropdown
                                {...field}
                                value={field.value || null}
                                onChange={field.onChange}
                                label={'GL Account'}
                                disabled={isLineBilled(idx)}
                                searchFilters={{
                                  isBankAccount: false,
                                  ...((payableType === PayableType.TRUST || payableType === PayableType.ESCROW) && {
                                    sortCode: PAYABLE_GL_SORT_CODE
                                  })
                                }}
                                sx={{ pt: 0 }}
                                errorMessage={
                                  formState.errors.lineItems?.[idx]?.glAccount?.value?.message ||
                                  formState.errors.lineItems?.[idx]?.glAccount?.message
                                }
                              />
                            )}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Box sx={{ pt: 0 }}>
                          <Controller
                            name={`lineItems.${idx}.amount`}
                            control={control}
                            render={({ field, fieldState: { invalid, error: { message } = {} } }) => (
                              <TextField
                                type="number"
                                margin="dense"
                                fullWidth
                                label="Amount"
                                disabled={isLineBilled(idx)}
                                error={invalid}
                                helperText={message ?? (isLineBilled(idx) ? 'On a bill — locked' : undefined)}
                                value={field.value ?? ''}
                                inputProps={{ step: 0.01 }}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                                }}
                                onChange={e => {
                                  const raw = e.target.value
                                  if (raw === '') {
                                    field.onChange('')
                                    return
                                  }
                                  const n = parseFloat(raw)
                                  if (!Number.isNaN(n)) {
                                    field.onChange(Math.round(n * 100) / 100)
                                  } else {
                                    field.onChange(raw)
                                  }
                                }}
                                onBlur={field.onBlur}
                                sx={{ my: 0 }}
                                variant="outlined"
                              />
                            )}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3.2}>
                        <Box sx={{ pt: 0 }}>
                          <Controller
                            name={`lineItems.${idx}.description`}
                            control={control}
                            render={({ field }) => (
                              <CustomController
                                controllerName={`lineItems.${idx}.description`}
                                controllerLabel="Description"
                                fieldType="text"
                                isDisabled={isLineBilled(idx)}
                                sx={{ my: 0 }}
                                onKeyDown={
                                  idx === lineItemFields.length - 1
                                    ? (e: React.KeyboardEvent) => {
                                        if (e.key === 'Tab' && !e.shiftKey) {
                                          e.preventDefault()
                                          handleAddLineItem()
                                        }
                                      }
                                    : undefined
                                }
                              />
                            )}
                          />
                        </Box>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={0.4}
                        sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}
                      >
                        <IconButton
                          size="small"
                          color="error"
                          aria-label="Remove Line Item"
                          onClick={() => remove(idx)}
                          // A paid line can be removed (its ledger rows are reversed on save) unless its
                          // expense is already on a bill.
                          disabled={lineItemFields.length === 1 || isLineBilled(idx)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                  <Grid item xs={12} mt={1}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={1}
                      px={1.5}
                      py={1}
                      borderRadius={1}
                      sx={{ bgcolor: (theme) => (hasLineItemMismatch ? alpha(theme.palette.error.main, 0.08) : theme.palette.action.hover) }}
                      border={1}
                      borderColor={hasLineItemMismatch ? 'error.main' : 'divider'}
                    >
                      <Typography variant="body2" color={hasLineItemMismatch ? 'error.main' : 'text.secondary'}>
                        Line items total&nbsp;
                        <strong>${(lineItemsTotalCents / 100).toFixed(2)}</strong>
                        &nbsp;of amount&nbsp;
                        <strong>${(payableAmountCents / 100).toFixed(2)}</strong>
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={hasLineItemMismatch ? 'error.main' : 'success.main'}
                      >
                        {hasLineItemMismatch
                          ? `${lineItemsDifferenceCents > 0 ? 'Over' : 'Short'} by $${(
                              Math.abs(lineItemsDifferenceCents) / 100
                            ).toFixed(2)} — these must match before saving`
                          : 'Matches'}
                      </Typography>
                    </Box>
                  </Grid>
                  {!isEditingPaid && (
                    <Grid item xs={12} display="flex" justifyContent="flex-start" mt={1}>
                      <Button size="small" variant="outlined" onClick={handleAddLineItem}>
                        + Add Line Item
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            ) : (
              <>
                <Grid item md={3} sm={6} xs={12} mt={1}>
                  <OfficeAutoComplete
                    name="office"
                    label="Profit Center"
                    disabled={isLineBilled(0)}
                    sx={{ paddingTop: 0 }}
                    currentOffice={
                      currentPayable?.lineItems?.[0]?.office
                        ? {
                            id: currentPayable.lineItems?.[0]?.office?.id,
                            name: currentPayable.lineItems?.[0]?.office?.name
                          }
                        : selectedVendorOffice?.value
                        ? {
                            id: selectedVendorOffice.value,
                            name: selectedVendorOffice.label
                          }
                        : undefined
                    }
                    autoSelectOnTab
                  />
                </Grid>
                <Grid item md={3} sm={6} xs={12} mt={1}>
                  <FormControl variant="outlined" fullWidth>
                    <Controller
                      name="glAccount"
                      control={control}
                      render={({ field }) => {
                        return (
                          <AccountDropdown
                            value={field.value}
                            onChange={field.onChange}
                            errorMessage={
                              formState.errors.glAccount?.value?.message || formState.errors.glAccount?.message
                            }
                            searchFilters={{
                              isBankAccount: false,
                              ...((payableType === PayableType.TRUST || payableType === PayableType.ESCROW) && {
                                sortCode: PAYABLE_GL_SORT_CODE
                              })
                            }}
                            label={'GL Account'}
                            disabled={isLineBilled(0)}
                            sx={{}}
                          />
                        )
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item md={3} sm={6} xs={12} mt={1}>
                  <MatterDropdownWithForm
                    name="matter"
                    userId=""
                    label="Matter"
                    disabled={isEditingPaid}
                    autoSelectOnTab
                    helperText={formState.errors.matter?.message}
                    initialCase={initialMatter}
                  />
                </Grid>
                {(payableType === PayableType.TRUST || payableType === PayableType.ESCROW) && (
                  <Grid item md={3} sm={6} xs={12} mt={1}>
                    <Controller
                      name={`lineItems.0.client`}
                      control={control}
                      render={({ field }) => (
                        <DepositClientDropdown
                          caseId={watch('matter') || undefined}
                          clientId={field.value ? field.value?.value : undefined}
                          disabled={isEditingPaid}
                          autoSelectOnTab
                          onChange={clientOption => {
                            field.onChange(
                              clientOption ? { value: clientOption.id, label: clientOption.label } : undefined
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}
                <Grid item md={3} sm={6} xs={12} mt={1}>
                  <CustomController
                    controllerName="lineItems.0.description"
                    controllerLabel="Line Item Description"
                    fieldType="text"
                    isDisabled={isLineBilled(0)}
                    sx={{ mt: 0 }}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <CheckboxController label="Mark as Paid" name="isPaid" size="small" disabled={isEditingPaid} />
              <Typography variant="body1" color="text.secondary" style={{ fontSize: '0.875rem' }}>
                {isEditingPaid
                  ? 'This payable is already paid and will stay paid; its ledger is updated in place on save.'
                  : 'This will mark the payable as paid and will update the Account Balance and General Ledger.'}
              </Typography>
            </Grid>
          </Grid>

          <DialogActions sx={{ padding: '30px 0 0' }}>
            <Button onClick={handleClose}>{CANCEL}</Button>

            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={loading || updating}
              endIcon={(loading || updating) && <CircularProgress size={20} color="inherit" />}
            >
              {currentPayable?.id ? 'Update' : 'Add'} Payable
            </Button>
          </DialogActions>
        </form>
      </FormProvider>

      {pendingFormData && (
        <PayableConfirmationModal
          isOpen={showConfirmationModal}
          onClose={() => {
            setShowConfirmationModal(false)
            setPendingFormData(null)
          }}
          onConfirm={handleConfirmedSubmit}
          loading={loading || updating}
          payableType={payableType}
          bankAccountLabel={pendingFormData.bankAccount?.label ?? ''}
          vendorLabel={pendingFormData.vendor?.label ?? ''}
          amount={Number(pendingFormData.amount) || 0}
          paymentMethod={getProperPaymentMethod(pendingFormData.paymentMethod as PaymentMethod)}
          checkOrRefNo={pendingFormData.checkOrAchNumber ?? ''}
          invoiceDate={pendingFormData.invoiceDate ?? ''}
          payDate={pendingFormData.payDate ?? ''}
          markAsPaid={!!pendingFormData.isPaid}
          notes={pendingFormData.description ?? ''}
        />
      )}

      <PayableLookupModal
        isOpen={lookupOpen}
        onClose={() => setLookupOpen(false)}
        keyword={watch('description') || ''}
        vendorId={watch('vendor')?.value}
      />
    </CustomModal>
  )
}

export default AddAccountPayableModal
